import { createContext, useContext, useEffect, useReducer } from 'react';

import {
  defaultPaginatedItemState,
  paginatedItemsReducer,
} from 'context/PaginatedItems/paginatedItemsReducer';
import {
  PageResponse,
  PaginatedItems,
  PaginatedItemsActions,
} from 'context/PaginatedItems/types/PaginatedItems';
import useFetchWithToken from 'hooks/useFetchWithToken';

const PaginatedItemsContext = createContext<PaginatedItems<any> | undefined>(undefined);

const PaginatedItemsProvider = <T,>({
  pageableUrl,
  pageSize,
  children,
}: {
  pageableUrl: string;
  pageSize: number;
  children?: React.ReactNode;
}) => {
  const [paginatedItemState, paginatedItemDispatch] = useReducer(
    paginatedItemsReducer<T>,
    defaultPaginatedItemState
  );
  const { fetchDataWithToken } = useFetchWithToken();

  useEffect(() => {
    loadItemsAndDispatch(1, PaginatedItemsActions.InitialLoad);
  }, [pageableUrl]);

  const loadMoreItems = async () => {
    loadItemsAndDispatch(paginatedItemState.page + 1, PaginatedItemsActions.LoadMore);
  };

  const loadItemsAndDispatch = async (page: number, action: PaginatedItemsActions) => {
    await fetchDataWithToken<PageResponse<T>>(getPageUrl(page), (pageResponse) => {
      paginatedItemDispatch({
        type: action,
        payload: pageResponse,
      });
    });
  };

  const getPageUrl = (page: number) => {
    const hasParams = pageableUrl.includes('?');
    if (hasParams) {
      return `${pageableUrl}&page=${page}&pageSize=${pageSize}`;
    }
    return `${pageableUrl}?page=${page}&pageSize=${pageSize}`;
  };

  const paginatedItemsValues: PaginatedItems<T> = {
    items: paginatedItemState.items,
    totalElements: paginatedItemState.totalElements,
    currentItems: paginatedItemState.items.length,
    canLoadMore: paginatedItemState.page < paginatedItemState.totalPages,
    loadMore: loadMoreItems,
  };

  return (
    <PaginatedItemsContext.Provider value={paginatedItemsValues}>
      {children}
    </PaginatedItemsContext.Provider>
  );
};

export const usePaginatedItemsContext: () => PaginatedItems<any> = () => {
  const paginatedItemsContext = useContext(PaginatedItemsContext);

  if (!paginatedItemsContext) {
    throw new Error('usePaginatedItemsContext must be used within PaginatedItemsProvider');
  }

  return paginatedItemsContext;
};

export default PaginatedItemsProvider;
